import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';

const AllGymsFinancials = ({ data, selectedPeriod }) => {
  const getStatusColor = (status) => {
    const colors = {
      excellent: 'var(--color-success)',
      good: 'var(--color-info)',
      warning: 'var(--color-warning)',
      critical: 'var(--color-error)'
    };
    return colors?.[status] || 'var(--color-muted)';
  };

  const getStatusIcon = (status) => {
    const icons = {
      excellent: 'TrendingUp',
      good: 'CheckCircle',
      warning: 'AlertTriangle',
      critical: 'XCircle'
    };
    return icons?.[status] || 'Minus';
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card bg-gradient-to-br from-primary/10 to-primary/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon name="DollarSign" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <h3 className="text-2xl font-heading font-semibold text-foreground">
                ${(data?.totalRevenue / 1000000)?.toFixed(2)}M
              </h3>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-error/10 to-error/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-error/20 flex items-center justify-center">
              <Icon name="CreditCard" size={20} color="var(--color-error)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Expenses</p>
              <h3 className="text-2xl font-heading font-semibold text-foreground">
                ${(data?.totalExpenses / 1000000)?.toFixed(2)}M
              </h3>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-success/10 to-success/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Net Profit</p>
              <h3 className="text-2xl font-heading font-semibold text-foreground">
                ${(data?.netProfit / 1000000)?.toFixed(2)}M
              </h3>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-accent/10 to-accent/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
              <Icon name="Percent" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Profit Margin</p>
              <h3 className="text-2xl font-heading font-semibold text-foreground">
                {data?.profitMargin}%
              </h3>
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
          Revenue & Profit Trend (Last 6 Months)
        </h3>
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.monthlyTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" style={{ fontSize: '14px' }} />
              <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '14px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '12px',
                  padding: '12px'
                }}
                formatter={(value) => `$${(value / 1000000)?.toFixed(2)}M`}
              />
              <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="var(--color-primary)"
                strokeWidth={3}
                name="Revenue"
                dot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke="var(--color-success)"
                strokeWidth={3}
                name="Profit"
                dot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
          Individual Gym Performance
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Gym Location</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Revenue</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Expenses</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Profit</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Margin</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Members</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {data?.gymBreakdown?.map((gym) => (
                <tr key={gym?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="Building2" size={16} color="var(--color-primary)" />
                      </div>
                      <span className="font-medium text-foreground">{gym?.name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right font-medium text-foreground">
                    ${gym?.revenue?.toLocaleString()}
                  </td>
                  <td className="py-4 px-4 text-right font-medium text-error">
                    ${gym?.expenses?.toLocaleString()}
                  </td>
                  <td className="py-4 px-4 text-right font-medium text-success">
                    ${gym?.profit?.toLocaleString()}
                  </td>
                  <td className="py-4 px-4 text-right font-medium text-foreground">
                    {gym?.margin}%
                  </td>
                  <td className="py-4 px-4 text-right font-medium text-foreground">
                    {gym?.members}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <Icon name={getStatusIcon(gym?.status)} size={16} color={getStatusColor(gym?.status)} />
                      <span
                        className="text-xs font-medium capitalize"
                        style={{ color: getStatusColor(gym?.status) }}
                      >
                        {gym?.status}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AllGymsFinancials;